import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  useTheme,
  alpha,
} from '@mui/material';
import { Translate, House, Campaign } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { getLanguage, setLanguage, t, Language } from '@/utils/i18n-enhanced';

const TutorialStepper: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const theme = useTheme();

  useEffect(() => {
    // Only show if the user hasn't completed it
    const hasSeenTutorial = localStorage.getItem('hasSeenTutorial');
    if (!hasSeenTutorial) {
      setOpen(true);
    }
  }, []);

  const handleClose = () => {
    localStorage.setItem('hasSeenTutorial', 'true');
    setOpen(false);
  };

  const handleNext = () => {
    if (activeStep === steps.length - 1) {
      handleClose();
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleLanguageSelect = (lang: Language) => {
    setLanguage(lang);
    // Force re-render to apply language change immediately within the modal
    setActiveStep(1); 
  };

  const steps = [
    { label: 'Language' },
    { label: 'Welcome' },
    { label: 'Roles' }
  ];

  return (
    <Dialog 
      open={open} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 24px 48px rgba(0,0,0,0.15)',
          overflow: 'hidden'
        }
      }}
    >
      <Box sx={{ p: 4, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((step) => (
            <Step key={step.label}>
              <StepLabel>{step.label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <DialogContent sx={{ minHeight: 300, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4 }}>
        <AnimatePresence mode="wait">
          {activeStep === 0 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              style={{ width: '100%', textAlign: 'center' }}
            >
              <Translate sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 4, fontFamily: '"Playfair Display", serif' }}>
                Choose Your Language<br/>Choisissez Votre Langue<br/>اختر لغتك
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button 
                  variant={getLanguage() === 'en' ? 'contained' : 'outlined'} 
                  onClick={() => handleLanguageSelect('en')}
                  size="large"
                >
                  English
                </Button>
                <Button 
                  variant={getLanguage() === 'fr' ? 'contained' : 'outlined'} 
                  onClick={() => handleLanguageSelect('fr')}
                  size="large"
                >
                  Français
                </Button>
                <Button 
                  variant={getLanguage() === 'ar' ? 'contained' : 'outlined'} 
                  onClick={() => handleLanguageSelect('ar')}
                  size="large"
                >
                  العربية
                </Button>
              </Box>
            </motion.div>
          )}

          {activeStep === 1 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              style={{ width: '100%', textAlign: 'center' }}
            >
              <House sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, fontFamily: '"Playfair Display", serif' }}>
                {t('tutorialWelcomeTitle')}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {t('tutorialWelcomeDesc')}
              </Typography>
            </motion.div>
          )}

          {activeStep === 2 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              style={{ width: '100%', textAlign: 'center' }}
            >
              <Campaign sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, fontFamily: '"Playfair Display", serif' }}>
                {t('tutorialRolesTitle')}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {t('tutorialRolesDesc')}
              </Typography>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0, justifyContent: 'space-between' }}>
        <Button 
          onClick={handleClose} 
          color="inherit" 
          disabled={activeStep === 0}
        >
          {activeStep === 0 ? '' : t('tutorialSkip')}
        </Button>
        <Button 
          onClick={handleNext} 
          variant="contained" 
          disabled={activeStep === 0}
          sx={{ px: 4 }}
        >
          {activeStep === steps.length - 1 ? t('tutorialFinish') : t('tutorialNext')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TutorialStepper;
